import { Injectable } from '@angular/core';

/** Known owner data-attribute names used across tab components. */
export const MODAL_OWNER_ATTRS = [
  'data-hubmessages-modal',
  'data-hubmessages-hl7viewer-modal',
  'data-create-modal',
  'data-interface-modal',
  'data-tryitout-modal',
  'data-requestform-modal',
  'data-w700jobslist-modal',
  'data-userrole-modal',
  'data-template-modal',
  'data-nonvitalcore-modal',
  'data-agent-modal',
  'data-agent-audit-modal',
  'data-logs-modal',
  'data-job-modal',
  'data-vendor-modal',
  'data-contactmodal-modal',
  'data-eventinfo-modal',
  'data-integrationmapping-modal',
  'data-bulkmessagereprocess-modal',
  'data-badmessage-modal',
  'data-badmessage-nested-modal',
  'data-badmessage-hl7viewer-modal',
  'data-case360-modal',
  'data-productagent-modal',
  'data-agentendpoint-modal',
  'data-manage-menu-modal',
  'data-processor-modal',
  'data-varules-modal',
  'data-ftpfiles-hl7viewer-modal',
] as const;

export type ModalOwnerAttr = (typeof MODAL_OWNER_ATTRS)[number] | string;

export interface ModalRefTracked {
  ref: any;
  windowClass: string;
}

export interface HideOwnedResult {
  modalWasOpen: boolean;
}

@Injectable({ providedIn: 'root' })
export class ModalTabVisibilityService {
  isOwnedByOther(el: HTMLElement, ownerAttr: ModalOwnerAttr): boolean {
    return MODAL_OWNER_ATTRS.some(
      (attr) => attr !== ownerAttr && el.hasAttribute(attr)
    );
  }

  hasAnyOwnerTag(el: HTMLElement): boolean {
    return MODAL_OWNER_ATTRS.some((attr) => el.hasAttribute(attr));
  }

  getVisibleBackdrops(): HTMLElement[] {
    return Array.from(document.querySelectorAll('.modal-backdrop')).filter(
      (backdrop) => {
        const style = window.getComputedStyle(backdrop);
        return style.display !== 'none' && style.display !== '';
      }
    ) as HTMLElement[];
  }

  isModalVisuallyOpen(modal: HTMLElement): boolean {
    const style = window.getComputedStyle(modal);
    return (
      modal.classList.contains('show') ||
      (style.display !== 'none' && style.display !== '')
    );
  }

  /**
   * Tag the matching modal and its backdrop with `ownerAttr`.
   * Prefer unique windowClass; never retag another owner's backdrop.
   */
  tagOwned(
    ownerAttr: ModalOwnerAttr,
    windowClass?: string,
    delayMs = 100
  ): void {
    setTimeout(() => {
      let modalElement: HTMLElement | null = null;
      if (windowClass) {
        // Support multi-class windowClass like "myCustomModalClass jobModalClass"
        const classes = windowClass.trim().split(/\s+/).filter(Boolean);
        if (classes.length > 0) {
          modalElement = document.querySelector(
            `.modal.${classes.join('.')}`
          ) as HTMLElement;
          if (!modalElement) {
            // Prefer the most specific (last) unique class
            const unique = classes[classes.length - 1];
            modalElement = document.querySelector(
              `.modal.${unique}.show, .modal.${unique}`
            ) as HTMLElement;
          }
        }
      }
      if (!modalElement) {
        const shown = Array.from(
          document.querySelectorAll('.modal.show')
        ) as HTMLElement[];
        modalElement =
          shown.reverse().find((m) => !this.isOwnedByOther(m, ownerAttr)) ||
          null;
      }
      if (modalElement && !this.isOwnedByOther(modalElement, ownerAttr)) {
        modalElement.setAttribute(ownerAttr, 'true');
      }

      const backdrops = document.querySelectorAll('.modal-backdrop.show');
      const backdrop = backdrops[backdrops.length - 1] as HTMLElement;
      if (
        backdrop &&
        !this.isOwnedByOther(backdrop, ownerAttr) &&
        (!this.hasAnyOwnerTag(backdrop) || backdrop.hasAttribute(ownerAttr))
      ) {
        backdrop.setAttribute(ownerAttr, 'true');
      }
    }, delayMs);
  }

  trackModalRef(
    tracked: ModalRefTracked[],
    refs: any[],
    modalRef: any,
    windowClass: string
  ): void {
    if (modalRef && !refs.includes(modalRef)) {
      refs.push(modalRef);
    }
    if (modalRef && !tracked.some((t) => t.ref === modalRef)) {
      tracked.push({ ref: modalRef, windowClass: windowClass || '' });
    }
  }

  untrackModalRef(
    tracked: ModalRefTracked[],
    refs: any[],
    modalRef: any
  ): void {
    const refIdx = refs.indexOf(modalRef);
    if (refIdx >= 0) {
      refs.splice(refIdx, 1);
    }
    const tIdx = tracked.findIndex((t) => t.ref === modalRef);
    if (tIdx >= 0) {
      tracked.splice(tIdx, 1);
    }
  }

  /**
   * Hide only modals/backdrops owned by this component.
   * Does not dismiss NgbModal — keeps DOM for restore on tab return.
   */
  hideOwned(
    ownerAttr: ModalOwnerAttr,
    options: {
      hasOpenRefs: boolean;
      windowClasses?: string[];
      extraSelectors?: string[];
      nestedOwnerAttrs?: string[];
    }
  ): HideOwnedResult {
    if (!options.hasOpenRefs) {
      return { modalWasOpen: false };
    }

    const windowClasses = (options.windowClasses || []).filter(
      (c) => c && c.trim()
    );
    let modalElements: HTMLElement[] = [];

    if (windowClasses.length > 0) {
      const selector = windowClasses
        .map((cls) => {
          const parts = cls.trim().split(/\s+/).filter(Boolean);
          return parts.length ? `.modal.${parts.join('.')}` : '';
        })
        .filter(Boolean)
        .join(', ');
      if (selector) {
        modalElements = Array.from(
          document.querySelectorAll(selector)
        ) as HTMLElement[];
      }
    }

    if (modalElements.length === 0) {
      modalElements = Array.from(
        document.querySelectorAll(`.modal[${ownerAttr}="true"]`)
      ) as HTMLElement[];
    }

    const extra = (options.extraSelectors || []).flatMap((sel) =>
      Array.from(document.querySelectorAll(sel))
    ) as HTMLElement[];

    const nestedAttrs = options.nestedOwnerAttrs || [];
    const nestedModals = nestedAttrs.flatMap(
      (attr) =>
        Array.from(
          document.querySelectorAll(`.modal[${attr}="true"]`)
        ) as HTMLElement[]
    );

    const toHide = [...modalElements, ...extra, ...nestedModals].filter(
      (m) =>
        this.isModalVisuallyOpen(m) ||
        m.hasAttribute(ownerAttr) ||
        nestedAttrs.some((a) => m.hasAttribute(a))
    );

    const unique = Array.from(new Set(toHide));

    if (unique.length === 0) {
      return { modalWasOpen: options.hasOpenRefs };
    }

    unique.forEach((modalElement) => {
      if (!this.isOwnedByOther(modalElement, ownerAttr)) {
        modalElement.setAttribute(ownerAttr, 'true');
      }
      modalElement.style.setProperty('display', 'none', 'important');
    });

    let backdrops = Array.from(
      document.querySelectorAll(`.modal-backdrop[${ownerAttr}="true"]`)
    ) as HTMLElement[];

    nestedAttrs.forEach((attr) => {
      backdrops = backdrops.concat(
        Array.from(
          document.querySelectorAll(`.modal-backdrop[${attr}="true"]`)
        ) as HTMLElement[]
      );
    });

    if (backdrops.length === 0) {
      // Claim only untagged visible backdrops — never steal other owners
      backdrops = this.getVisibleBackdrops().filter(
        (b) => !this.hasAnyOwnerTag(b) || b.hasAttribute(ownerAttr)
      );
      backdrops.forEach((b) => {
        if (!this.isOwnedByOther(b, ownerAttr)) {
          b.setAttribute(ownerAttr, 'true');
        }
      });
    }

    Array.from(new Set(backdrops)).forEach((backdrop) => {
      if (!this.isOwnedByOther(backdrop, ownerAttr)) {
        backdrop.setAttribute(ownerAttr, 'true');
      }
      backdrop.style.setProperty('display', 'none', 'important');
    });

    document.body.classList.remove('modal-open');
    return { modalWasOpen: true };
  }

  /**
   * Restore only modals/backdrops tagged with this owner.
   */
  showOwned(
    ownerAttr: ModalOwnerAttr,
    options: {
      modalWasOpen: boolean;
      hasOpenRefs: boolean;
      windowClasses?: string[];
      extraSelectors?: string[];
      nestedOwnerAttrs?: string[];
      delayMs?: number;
    }
  ): void {
    if (!options.modalWasOpen || !options.hasOpenRefs) {
      return;
    }

    const run = () => {
      const windowClasses = (options.windowClasses || []).filter(
        (c) => c && c.trim()
      );
      let modalElements: HTMLElement[] = [];

      if (windowClasses.length > 0) {
        const selector = windowClasses
          .map((cls) => {
            const parts = cls.trim().split(/\s+/).filter(Boolean);
            return parts.length
              ? `.modal.${parts.join('.')}[${ownerAttr}="true"]`
              : '';
          })
          .filter(Boolean)
          .join(', ');
        if (selector) {
          modalElements = Array.from(
            document.querySelectorAll(selector)
          ) as HTMLElement[];
        }
      }

      if (modalElements.length === 0) {
        modalElements = Array.from(
          document.querySelectorAll(`.modal[${ownerAttr}="true"]`)
        ) as HTMLElement[];
      }

      const extra = (options.extraSelectors || []).flatMap((sel) =>
        Array.from(document.querySelectorAll(sel))
      ) as HTMLElement[];

      const nestedAttrs = options.nestedOwnerAttrs || [];
      const nestedModals = nestedAttrs.flatMap(
        (attr) =>
          Array.from(
            document.querySelectorAll(`.modal[${attr}="true"]`)
          ) as HTMLElement[]
      );

      const toShow = Array.from(
        new Set([...modalElements, ...extra, ...nestedModals])
      );
      if (toShow.length === 0) {
        return;
      }

      toShow.forEach((modalElement) => {
        modalElement.style.setProperty('display', 'block', 'important');
        if (!modalElement.classList.contains('show')) {
          modalElement.classList.add('show');
        }
      });

      let backdrops = Array.from(
        document.querySelectorAll(`.modal-backdrop[${ownerAttr}="true"]`)
      ) as HTMLElement[];
      nestedAttrs.forEach((attr) => {
        backdrops = backdrops.concat(
          Array.from(
            document.querySelectorAll(`.modal-backdrop[${attr}="true"]`)
          ) as HTMLElement[]
        );
      });

      Array.from(new Set(backdrops)).forEach((backdrop) => {
        backdrop.style.setProperty('display', 'block', 'important');
        if (!backdrop.classList.contains('show')) {
          backdrop.classList.add('show');
        }
      });

      document.body.classList.add('modal-open');
    };

    if (options.delayMs && options.delayMs > 0) {
      setTimeout(run, options.delayMs);
    } else {
      run();
    }
  }

  /**
   * On tab enter: hide any visible modals/backdrops owned by other components.
   */
  hideForeignOwnedModals(ownAttr: ModalOwnerAttr): void {
    let hidForeign = false;

    MODAL_OWNER_ATTRS.forEach((attr) => {
      if (attr === ownAttr) {
        return;
      }
      const modals = Array.from(
        document.querySelectorAll(`.modal[${attr}="true"]`)
      ) as HTMLElement[];
      modals.forEach((modal) => {
        if (this.isModalVisuallyOpen(modal)) {
          modal.style.setProperty('display', 'none', 'important');
          hidForeign = true;
        }
      });

      const extras = Array.from(
        document.querySelectorAll(
          `#newPopupModal[${attr}="true"], .custom-modal[${attr}="true"]`
        )
      ) as HTMLElement[];
      extras.forEach((modal) => {
        const style = window.getComputedStyle(modal);
        if (style.display !== 'none' && style.display !== '') {
          modal.style.setProperty('display', 'none', 'important');
          hidForeign = true;
        }
      });

      const backdrops = Array.from(
        document.querySelectorAll(`.modal-backdrop[${attr}="true"]`)
      ) as HTMLElement[];
      backdrops.forEach((backdrop) => {
        const style = window.getComputedStyle(backdrop);
        if (style.display !== 'none' && style.display !== '') {
          backdrop.style.setProperty('display', 'none', 'important');
          hidForeign = true;
        }
      });
    });

    if (hidForeign) {
      const ownVisible = Array.from(
        document.querySelectorAll(`.modal[${ownAttr}="true"]`)
      ).some((m) => this.isModalVisuallyOpen(m as HTMLElement));
      if (!ownVisible) {
        document.body.classList.remove('modal-open');
      }
    }
  }

  closeAllRefs(refs: any[]): void {
    (refs || []).forEach((modalRef) => {
      if (!modalRef) {
        return;
      }
      try {
        modalRef.close();
      } catch {
        // already closed
      }
    });
  }
}
